import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Download, Gift, Receipt as ReceiptIcon, Trash2 } from 'lucide-react';
import {
  ORDER_STATUSES,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  formatRate,
  type InvoiceSummary,
  type OrderExportRow,
  type OrderSummary,
  type OrderStatus,
  type PaymentMethod,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { downloadXlsx, xlsxDateTime, type XlsxCell, type XlsxColumn } from '../lib/xlsx';
import { useToast } from '../hooks/useToast';
import { useSettings, useTables, useUsers } from '../hooks/queries';
import { useCurrency } from '../lib/currency';
import { formatClock, formatDate, formatDateTime, titleCase } from '../lib/format';
import { CloseOrderDialog, InvoiceBuyerSummary } from '../components/InvoiceBuyerDialog';
import { GuestMixBadges, GuestMixDialog } from '../components/GuestMix';
import { PrintReceiptButton, ReceiptSheet } from '../components/ReceiptSheet';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Stat,
} from '../components/ui';
import { useAuth } from '../lib/auth';

/** Any closed order with something on the bill carries an e-invoice — invited tables included. */
const invoiceable = (order: Pick<OrderSummary, 'status' | 'grandTotalCents'>) =>
  order.status === 'CLOSED' && order.grandTotalCents > 0;

function InvoiceBadge({ invoice, eligible }: { invoice: InvoiceSummary | null | undefined; eligible: boolean }) {
  if (!eligible) return <span className="text-ink-faint">—</span>;
  if (!invoice) return <Badge tone="neutral">Not issued</Badge>;
  if (invoice.status === 'ISSUED') {
    return <Badge tone="positive">No. {invoice.invoiceNo}</Badge>;
  }
  if (invoice.status === 'FAILED') {
    return (
      <span title={invoice.errorMessage ?? undefined}>
        <Badge tone="danger">Failed</Badge>
      </span>
    );
  }
  return <Badge tone="caution">Pending</Badge>;
}

/**
 * An order can be removed while its table is still in service and no money has
 * been taken on it. Removing cancels it: it stays listed (dimmed) and can still
 * be opened, but it frees the table and is never invoiced.
 */
const removable = (order: Pick<OrderSummary, 'status' | 'paidCents'>) =>
  (order.status === 'OPEN' || order.status === 'IN_PROGRESS' || order.status === 'WAITING_PAYMENT') &&
  order.paidCents === 0;

const STATUS_TONE: Record<OrderStatus, 'neutral' | 'accent' | 'positive' | 'caution' | 'danger'> = {
  OPEN: 'accent',
  IN_PROGRESS: 'accent',
  WAITING_PAYMENT: 'caution',
  PAID: 'positive',
  CLOSED: 'neutral',
  CANCELLED: 'danger',
};

/** The export's columns; `money` is the currency's number format (whole đồng, or two decimals). */
const exportColumns = (money: string): XlsxColumn[] => [
  { header: 'Order #', width: 9 },
  { header: 'Opened', width: 17, format: 'yyyy-mm-dd hh:mm' },
  { header: 'Closed', width: 17, format: 'yyyy-mm-dd hh:mm' },
  { header: 'Table', width: 8 },
  { header: 'Guests', width: 8 },
  { header: 'Staff', width: 16 },
  { header: 'Status', width: 12 },
  { header: 'Invite', width: 8 },
  { header: 'Payment', width: 16 },
  { header: 'Source', width: 14 },
  { header: 'Guest type', width: 14 },
  { header: 'Item', width: 28 },
  { header: 'Modifiers', width: 24 },
  { header: 'Qty', width: 6 },
  { header: 'Unit price', width: 13, format: money },
  { header: 'Line subtotal', width: 14, format: money },
  { header: 'Tax type', width: 10 },
  { header: 'Tax rate %', width: 10 },
  { header: 'Item status', width: 12 },
  { header: 'Item notes', width: 24 },
  { header: 'Order subtotal', width: 15, format: money },
  { header: 'Discount', width: 13, format: money },
  { header: 'Service', width: 13, format: money },
  { header: 'Tax', width: 13, format: money },
  { header: 'Grand total', width: 15, format: money },
  { header: 'Paid', width: 15, format: money },
  { header: 'Tip', width: 13, format: money },
  { header: 'Order notes', width: 28 },
];

/**
 * One row per item line, with the order's details repeated so the sheet can be
 * filtered and sorted freely. The order totals are written on the first line of
 * each order only, so summing a totals column never counts a bill twice.
 */
function buildExportRows(orders: OrderExportRow[], amount: (cents: number) => number): XlsxCell[][] {
  const rows: XlsxCell[][] = [];
  for (const order of orders) {
    const orderCells: XlsxCell[] = [
      order.orderNumber,
      xlsxDateTime(order.openedAt),
      xlsxDateTime(order.closedAt),
      order.tableNumber,
      order.guestCount,
      order.staffName ?? '',
      titleCase(order.status),
      order.isInvite ? 'Yes' : '',
      order.paymentMethods.map((method) => PAYMENT_METHOD_LABELS[method]).join(', '),
      order.guestSource ?? '',
      order.guestStatus ?? '',
    ];
    const totalCells: XlsxCell[] = [
      amount(order.subtotalCents),
      amount(order.discountCents),
      amount(order.serviceChargeCents),
      amount(order.taxCents),
      amount(order.grandTotalCents),
      amount(order.paidCents),
      // Outside the bill: never part of the grand total beside it.
      amount(order.tipCents),
      order.notes ?? '',
    ];
    const blankTotals: XlsxCell[] = totalCells.map(() => null);

    if (order.items.length === 0) {
      rows.push([...orderCells, ...Array<XlsxCell>(9).fill(null), ...totalCells]);
      continue;
    }
    order.items.forEach((item, index) => {
      rows.push([
        ...orderCells,
        item.nameSnapshot,
        item.modifiers.join('; '),
        item.quantity,
        amount(item.unitPriceCents),
        amount(item.lineSubtotalCents),
        item.taxType === 'ALCOHOL' ? 'Alcohol' : 'Food',
        Number(formatRate(item.taxRateBp)),
        titleCase(item.status),
        [item.notes, item.cancelledReason ? `Voided: ${item.cancelledReason}` : null]
          .filter(Boolean)
          .join(' — '),
        ...(index === 0 ? totalCells : blankTotals),
      ]);
    });
  }
  return rows;
}

function startOfToday(): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 10);
}

/** Order history with the filters the specification calls for. */
export function OrderHistoryPage() {
  const { money, decimals } = useCurrency();
  const toast = useToast();
  const [exporting, setExporting] = useState(false);
  const { allows, user } = useAuth();
  const { data: tables } = useTables(true, false);
  const { data: staff } = useUsers({ enabled: allows('admin:users') });

  const [from, setFrom] = useState(startOfToday());
  const [to, setTo] = useState('');
  const [tableId, setTableId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  /** '' = every order, 'true' = invited only, 'false' = paying tables only. */
  const [invited, setInvited] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
      tableId: tableId || undefined,
      staffId: staffId || undefined,
      status: status || undefined,
      paymentMethod: paymentMethod || undefined,
      isInvite: invited || undefined,
      page,
      pageSize: 25,
    }),
    [from, to, tableId, staffId, status, paymentMethod, invited, page],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orders', 'history', params],
    queryFn: () => api.orders.history(params),
  });

  const { data: detail } = useQuery({
    queryKey: ['order', openOrderId ?? 'none'],
    queryFn: () => api.orders.get(openOrderId!),
    enabled: Boolean(openOrderId),
  });

  const { data: receipt } = useQuery({
    queryKey: ['receipt', openOrderId ?? 'none'],
    queryFn: () => api.orders.receipt(openOrderId!),
    enabled: Boolean(openOrderId),
  });

  /* ---------------------------- e-invoicing ---------------------------- */
  const queryClient = useQueryClient();
  const { data: settings } = useSettings();
  const canIssue = Boolean(settings?.invoicingEnabled) && allows('payments:process');

  const { data: detailInvoice } = useQuery({
    queryKey: ['invoice', openOrderId ?? 'none'],
    queryFn: () => api.orders.invoice(openOrderId!),
    enabled: Boolean(openOrderId),
  });

  // The order whose invoice is about to be issued, once the cashier says who it is for.
  const [issuingOrderId, setIssuingOrderId] = useState<string | null>(null);
  const { data: issuingOrder } = useQuery({
    queryKey: ['order', issuingOrderId ?? 'none'],
    queryFn: () => api.orders.get(issuingOrderId!),
    enabled: Boolean(issuingOrderId),
  });

  const issueInvoice = useMutation({
    mutationFn: (orderId: string) => api.orders.issueInvoice(orderId),
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'history'] });
      void queryClient.invalidateQueries({ queryKey: ['invoice'] });
      setIssuingOrderId(null);
      if (invoice.status === 'ISSUED') toast.success(`Invoice ${invoice.invoiceNo} issued.`);
      else toast.error(`EasyInvoice refused it: ${invoice.errorMessage ?? 'unknown error'}`);
    },
    onError: (caught: unknown) => {
      setIssuingOrderId(null);
      toast.error(caught instanceof ApiError ? caught.message : 'Could not issue the invoice.');
    },
  });

  /* ---------------------------- guest & tip ----------------------------- */
  const canEditGuest = allows('pos:operate');
  const [editingGuest, setEditingGuest] = useState(false);

  /* ------------------------------ removing ------------------------------ */
  /** Admins only; the server enforces this and asks for the admin's password again. */
  const canRemove = user?.role === 'ADMIN';
  const [removingOrder, setRemovingOrder] = useState<Pick<OrderSummary, 'id' | 'orderNumber' | 'tableNumber'> | null>(
    null,
  );
  const [removePassword, setRemovePassword] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);

  const startRemoving = (order: Pick<OrderSummary, 'id' | 'orderNumber' | 'tableNumber'>) => {
    setRemovePassword('');
    setRemoveError(null);
    setRemovingOrder(order);
  };
  const stopRemoving = () => {
    setRemovingOrder(null);
    setRemovePassword('');
    setRemoveError(null);
  };

  const removeOrder = useMutation({
    mutationFn: ({ orderId, password }: { orderId: string; password: string }) =>
      api.orders.remove(orderId, password),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      void queryClient.invalidateQueries({ queryKey: ['receipt', order.id] });
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      stopRemoving();
      toast.success(`Order #${order.orderNumber} removed — Table ${order.tableNumber} is available.`);
    },
    onError: (caught: unknown) => {
      // A wrong password keeps the dialog open so it can be typed again.
      if (caught instanceof ApiError && caught.code === 'INVALID_PASSWORD') {
        setRemoveError(caught.message);
        setRemovePassword('');
        return;
      }
      stopRemoving();
      toast.error(caught instanceof ApiError ? caught.message : 'Could not remove the order.');
    },
  });

  const confirmRemove = () => {
    if (!removingOrder || removeOrder.isPending) return;
    if (!removePassword) {
      setRemoveError('Enter your password to confirm.');
      return;
    }
    removeOrder.mutate({ orderId: removingOrder.id, password: removePassword });
  };

  const needsIssue = (invoice: InvoiceSummary | null | undefined) => !invoice || invoice.status === 'FAILED';

  const exportOrders = async () => {
    setExporting(true);
    try {
      const { page: _page, pageSize: _pageSize, ...filters } = params;
      const orders = await api.orders.exportHistory(filters);
      if (orders.length === 0) {
        toast.error('No orders match these filters — nothing to export.');
        return;
      }
      // Real numbers, shown with the currency's decimals, so the sheet can sum them.
      const amount = (cents: number) => (decimals ? cents / 100 : Math.round(cents / 100));
      const range = [from, to].filter(Boolean).join('_to_') || 'all';
      await downloadXlsx(
        `orders_${range}.xlsx`,
        'Orders',
        exportColumns(decimals ? '#,##0.00' : '#,##0'),
        buildExportRows(orders, amount),
      );
      toast.success(`Exported ${orders.length} ${orders.length === 1 ? 'order' : 'orders'}.`);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not export the orders.');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const resetFilters = () => {
    setFrom(startOfToday());
    setTo('');
    setTableId('');
    setStaffId('');
    setStatus('');
    setPaymentMethod('');
    setInvited('');
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-[1500px] px-5 py-6">
      <header className="mb-6">
        <h1 className="font-display text-3xl tracking-wide text-ink">Order History</h1>
        <p className="mt-1 text-sm text-ink-faint">
          Every order is kept with the prices, tax rates and modifiers that applied at the time.
        </p>
      </header>

      <Card className="mb-5">
        <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
          <Field label="From">
            <Input type="date" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
          </Field>
          <Field label="Table">
            <Select value={tableId} onChange={(event) => { setTableId(event.target.value); setPage(1); }}>
              <option value="">All tables</option>
              {(tables ?? []).map((table) => (
                <option key={table.id} value={table.id}>
                  Table {table.number}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Staff">
            <Select value={staffId} onChange={(event) => { setStaffId(event.target.value); setPage(1); }}>
              <option value="">All staff</option>
              {(staff ?? []).map((member) => (
                <option key={member.id} value={member.id}>
                  {member.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select
              value={status}
              onChange={(event) => { setStatus(event.target.value as OrderStatus | ''); setPage(1); }}
            >
              <option value="">Any status</option>
              {ORDER_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Payment">
            <Select
              value={paymentMethod}
              onChange={(event) => { setPaymentMethod(event.target.value as PaymentMethod | ''); setPage(1); }}
            >
              <option value="">Any method</option>
              {PAYMENT_METHODS.map((value) => (
                <option key={value} value={value}>
                  {PAYMENT_METHOD_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Invites">
            <Select
              value={invited}
              onChange={(event) => {
                setInvited(event.target.value as '' | 'true' | 'false');
                setPage(1);
              }}
            >
              <option value="">All orders</option>
              <option value="false">Paying tables only</option>
              <option value="true">Invited only</option>
            </Select>
          </Field>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            Reset filters
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Download size={15} />}
            loading={exporting}
            disabled={!data || data.total === 0}
            onClick={() => void exportOrders()}
          >
            Export XLSX
          </Button>
        </div>
      </Card>

      {data ? (
        <div className="mb-5 flex flex-wrap gap-8 rounded-card border border-line bg-surface px-5 py-4">
          <Stat label="Orders" value={data.summary.orderCount} />
          <Stat label="Guests" value={data.summary.guestCount} />
          <Stat label="Gross" value={money(data.summary.grossCents)} tone="accent" />
          <Stat label="Tax" value={money(data.summary.taxCents)} />
          <Stat label="Service" value={money(data.summary.serviceChargeCents)} />
          {/*
            Invited tables are shown but never folded into the figures above, so
            the boss can see what the house gave away without it flattering or
            denting the day's takings.
          */}
          {data.summary.invitedCount > 0 ? (
            <Stat
              label={`Invited (${data.summary.invitedCount})`}
              value={money(data.summary.invitedCents)}
            />
          ) : null}
          {data.summary.tipCents > 0 ? (
            <Stat label="Tips" value={money(data.summary.tipCents)} />
          ) : null}
          {/* Removed orders stay in the list below but are counted in none of the figures above. */}
          {data.summary.removedCount > 0 ? (
            <Stat label="Removed" value={data.summary.removedCount} />
          ) : null}
        </div>
      ) : null}

      {isLoading ? <Spinner label="Loading orders…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {data && data.data.length === 0 ? (
        <EmptyState
          icon={<ClipboardList size={30} />}
          title="No orders found"
          message="Try widening the date range or clearing the filters."
        />
      ) : null}

      {data && data.data.length > 0 ? (
        <div className="overflow-x-auto rounded-card border border-line">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-raised/60 text-left text-[11px] uppercase tracking-wider text-ink-faint">
              <tr>
                <th className="px-4 py-3 font-semibold">Order</th>
                <th className="px-4 py-3 font-semibold">Table</th>
                <th className="px-4 py-3 font-semibold">Guests</th>
                <th className="px-4 py-3 font-semibold">Opened</th>
                <th className="px-4 py-3 font-semibold">Closed</th>
                <th className="px-4 py-3 font-semibold">Staff</th>
                <th className="px-4 py-3 font-semibold">Payment</th>
                <th className="px-4 py-3 font-semibold">Guest</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Invoice</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.data.map((order) => (
                <tr
                  key={order.id}
                  onClick={() => setOpenOrderId(order.id)}
                  className={
                    'cursor-pointer border-t border-line transition hover:bg-hover ' +
                    (order.status === 'CANCELLED' ? 'opacity-45' : '')
                  }
                  title={order.status === 'CANCELLED' ? 'Removed — click to view' : undefined}
                >
                  <td className="tnum px-4 py-3 font-medium text-ink">#{order.orderNumber}</td>
                  <td className="px-4 py-3 text-ink-soft">Table {order.tableNumber}</td>
                  <td className="tnum px-4 py-3 text-ink-soft">{order.guestCount}</td>
                  <td className="px-4 py-3 text-ink-soft">
                    {formatDate(order.openedAt)} · {formatClock(order.openedAt)}
                  </td>
                  <td className="px-4 py-3 text-ink-faint">
                    {order.closedAt ? formatClock(order.closedAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{order.staffName ?? '—'}</td>
                  <td className="px-4 py-3 text-ink-faint">
                    {order.paymentMethods.map((method) => PAYMENT_METHOD_LABELS[method]).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <GuestMixBadges source={order.guestSource} status={order.guestStatus} />
                    {order.tipCents > 0 ? (
                      <div className="tnum mt-1 text-[11px] text-ink-faint">Tip {money(order.tipCents)}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge tone={STATUS_TONE[order.status]}>{titleCase(order.status)}</Badge>
                      {order.isInvite ? (
                        <Badge tone="accent">
                          <Gift size={11} className="mr-1 inline-block align-[-1px]" />
                          Invite
                        </Badge>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <InvoiceBadge invoice={order.invoice} eligible={invoiceable(order)} />
                      {canIssue && invoiceable(order) && needsIssue(order.invoice) ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={issueInvoice.isPending && issuingOrderId === order.id}
                          onClick={() => setIssuingOrderId(order.id)}
                        >
                          {order.invoice?.status === 'FAILED' ? 'Retry' : 'Issue'}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                  <td
                    className={
                      'tnum px-4 py-3 text-right font-semibold ' +
                      (order.isInvite ? 'text-ink-faint line-through' : 'text-accent-strong')
                    }
                    title={order.isInvite ? 'On the house — not counted in the takings' : undefined}
                  >
                    {money(order.grandTotalCents)}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(event) => event.stopPropagation()}>
                    {canRemove && removable(order) ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 size={14} />}
                        loading={removeOrder.isPending && removingOrder?.id === order.id}
                        onClick={() => startRemoving(order)}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {data && totalPages > 1 ? (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-ink-faint">
            Page {data.page} of {totalPages} · {data.total} orders
          </span>
          <div className="flex gap-2">
            <Button size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
              Previous
            </Button>
            <Button size="sm" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <Modal
        open={openOrderId !== null}
        onClose={() => setOpenOrderId(null)}
        title={detail ? `Order #${detail.orderNumber}` : 'Order'}
        subtitle={
          detail
            ? `Table ${detail.tableNumber} · ${formatDateTime(detail.openedAt)}${
                detail.isInvite ? ' · Invite — on the house' : ''
              }${detail.status === 'CANCELLED' ? ' · Removed' : ''}`
            : undefined
        }
        size="xl"
        footer={
          <>
            {detail && canRemove && removable(detail) ? (
              <Button
                variant="danger"
                className="mr-auto"
                icon={<Trash2 size={15} />}
                onClick={() => startRemoving(detail)}
              >
                Remove order
              </Button>
            ) : null}
            <PrintReceiptButton />
          </>
        }
      >
        {!detail ? (
          <Spinner />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_24rem]">
            <div>
              <h3 className="mb-3 font-display text-lg text-ink">Items</h3>
              <div className="space-y-2">
                {detail.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-line bg-canvas px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <div
                        className={
                          'text-sm ' +
                          (item.status === 'CANCELLED' ? 'text-ink-faint line-through' : 'text-ink')
                        }
                      >
                        <span className="tnum text-accent">{item.quantity}×</span> {item.nameSnapshot}
                        <span className="ml-2 text-xs text-ink-faint">
                          @ {money(item.unitPriceCents)} · {item.taxType === 'ALCOHOL' ? 'Alcohol' : 'Food'}{' '}
                          {formatRate(item.taxRateBp)}%
                        </span>
                      </div>
                      {item.modifiers.length > 0 ? (
                        <div className="text-[11px] text-ink-faint">
                          {item.modifiers.map((modifier) => modifier.optionName).join(' · ')}
                        </div>
                      ) : null}
                      {item.notes ? (
                        <div className="text-[11px] italic text-caution">{item.notes}</div>
                      ) : null}
                      <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-ink-faint">
                        <span>Course {item.course}</span>
                        <span>Added {formatClock(item.createdAt)}</span>
                        {item.sentAt ? <span>Sent {formatClock(item.sentAt)}</span> : null}
                        {item.readyAt ? <span>Ready {formatClock(item.readyAt)}</span> : null}
                        {item.cancelledAt ? (
                          <span className="text-danger">
                            Voided {formatClock(item.cancelledAt)} — {item.cancelledReason}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <span className="tnum shrink-0 text-sm text-ink-soft">
                      {money(item.lineSubtotalCents)}
                    </span>
                  </div>
                ))}
              </div>

              <h3 className="mb-3 mt-6 font-display text-lg text-ink">Guest &amp; tip</h3>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-canvas px-3 py-3 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <GuestMixBadges source={detail.guestSource} status={detail.guestStatus} />
                  <span className="tnum text-ink-soft">
                    Tip {detail.tipCents > 0 ? money(detail.tipCents) : '—'}
                  </span>
                </div>
                {canEditGuest && detail.status !== 'CANCELLED' ? (
                  <Button size="sm" variant="secondary" onClick={() => setEditingGuest(true)}>
                    Edit
                  </Button>
                ) : null}
              </div>

              {invoiceable(detail) ? (
                <>
                  <h3 className="mb-3 mt-6 font-display text-lg text-ink">E-invoice</h3>
                  <div className="space-y-2 rounded-lg border border-line bg-canvas px-3 py-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <InvoiceBadge invoice={detailInvoice} eligible />
                      {canIssue && needsIssue(detailInvoice) ? (
                        <Button
                          size="sm"
                          variant="primary"
                          loading={issueInvoice.isPending && issuingOrderId === detail.id}
                          onClick={() => setIssuingOrderId(detail.id)}
                        >
                          {detailInvoice?.status === 'FAILED' ? 'Retry invoice' : 'Issue invoice'}
                        </Button>
                      ) : null}
                    </div>
                    <InvoiceBuyerSummary order={detail} />
                    {detailInvoice?.status === 'ISSUED' && detailInvoice.issuedAt ? (
                      <p className="text-xs text-ink-faint">Issued {formatDateTime(detailInvoice.issuedAt)}</p>
                    ) : null}
                    {detailInvoice?.status === 'FAILED' ? (
                      <p className="text-xs text-danger">
                        {detailInvoice.errorMessage} · {detailInvoice.attempts}{' '}
                        {detailInvoice.attempts === 1 ? 'attempt' : 'attempts'}
                      </p>
                    ) : null}
                    {!settings?.invoicingEnabled && needsIssue(detailInvoice) ? (
                      <p className="text-xs text-ink-faint">Switch e-invoicing on in Settings to issue it.</p>
                    ) : null}
                  </div>
                </>
              ) : null}

              {detail.payments.length > 0 ? (
                <>
                  <h3 className="mb-3 mt-6 font-display text-lg text-ink">Payments</h3>
                  <div className="space-y-2">
                    {detail.payments.map((payment) => (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between rounded-lg border border-line bg-canvas px-3 py-2.5 text-sm"
                      >
                        <div>
                          <span className={payment.isVoided ? 'text-ink-faint line-through' : 'text-ink'}>
                            {PAYMENT_METHOD_LABELS[payment.method]}
                          </span>
                          <span className="ml-2 text-xs text-ink-faint">
                            {formatClock(payment.paidAt)}
                            {payment.staffName ? ` · ${payment.staffName}` : ''}
                            {payment.reference ? ` · ${payment.reference}` : ''}
                          </span>
                        </div>
                        <span className="tnum text-ink-soft">{money(payment.amountCents)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>

            <div>
              {receipt ? (
                <ReceiptSheet receipt={receipt} />
              ) : (
                <Card>
                  <EmptyState icon={<ReceiptIcon size={26} />} title="Receipt loading" />
                </Card>
              )}
            </div>
          </div>
        )}
      </Modal>

      {detail ? (
        <GuestMixDialog order={detail} open={editingGuest} onClose={() => setEditingGuest(false)} />
      ) : null}

      <ConfirmDialog
        open={removingOrder !== null}
        title={removingOrder ? `Remove order #${removingOrder.orderNumber}?` : 'Remove order?'}
        message={
          removingOrder
            ? `Every item is voided, its kitchen and bar tickets are cleared and Table ${removingOrder.tableNumber} becomes available. The order stays in the history, dimmed, and can still be opened — but no invoice can be issued for it.`
            : ''
        }
        confirmLabel="Remove order"
        loading={removeOrder.isPending}
        onConfirm={confirmRemove}
        onCancel={stopRemoving}
      >
        <Field label={`Password for ${user?.email ?? 'your account'}`}>
          <Input
            type="password"
            autoFocus
            autoComplete="current-password"
            value={removePassword}
            onChange={(event) => {
              setRemovePassword(event.target.value);
              setRemoveError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') confirmRemove();
            }}
          />
        </Field>
        {removeError ? <p className="mt-2 text-xs text-danger">{removeError}</p> : null}
      </ConfirmDialog>

      {issuingOrder && issuingOrderId ? (
        <CloseOrderDialog
          purpose="issue"
          order={issuingOrder}
          open
          closing={issueInvoice.isPending}
          onClose={() => setIssuingOrderId(null)}
          onConfirm={() => issueInvoice.mutate(issuingOrder.id)}
        />
      ) : null}
    </div>
  );
}
